import _ from "lodash";

import { Member } from "src/patents/patents";
import { missingInformation, requiresGrant } from "./check";
import { DueDateStatus, InstructionType, IpRight, Renewal } from "../interfaces/renewal_types";
// import { Instruction, IpRight, MaintenanceAction, MaintenanceActionPhase, PcInstruction, PcIpRight, ValidationMessage } from "../DennemeyerProvider";
// import { DueDateStatus } from "../settings/instruction_timeline";

// Preparing functions for Notion Flow Chart 
// https://www.notion.so/patent-cockpit/Flow-Charts-for-Renewal-States-566df9d644f34769a7b66f368526600c?pvs=4

/*************************
 * 
 * THIS IS THE COMMON/DUMMY/REFERENCE IMPLEMENTATION
 * 
 * E.g Dennemeyer has a different implementation
 * 
 *************************
 */

export type IpRightImportStatus =
    'not-handled' |
    'ready' 

export type IpRightImportSubStatus =
    'ignored' |
    'stopped' |
    'inactive' |
    'missing-data' |
    'has-errors' |
    'can-import' |
    'paid-for-life' |
    'back-office-processing' |
    'fully-ready'

export type IpRightImportStatusInfo = {
    status: IpRightImportStatus,
    substatus: IpRightImportSubStatus,
    inReview?: boolean,
    inSurcharge?: boolean,
    messages?: string[],
    reason?: string,
}

export type ValidationMessage = {
    message: string,
}

// TODO: just focus on not-handled cases
export function importStatusToString(status: IpRightImportStatusInfo, t: (key: string, options?: object) => string): string {
    //console.log({status})
    if (status.inReview) {
        if (status.substatus === 'stopped') 
            return t('stopped-in-pending-review')
        else if (status.status === 'ready') 
            return t('paying-in-pending-review')
    } 
    if (status.status === 'not-handled') {
        if (status.substatus === 'ignored' && status.reason) {
            return `${t(status.status)} (${t(status.reason)})`
        } else if (status.substatus === 'missing-data' || status.substatus === 'has-errors') {
            return `${t(status.status)} (${t(status.substatus)})`
        } else if (status.substatus === 'inactive' || status.substatus === 'stopped') {
            return `${t(status.status)} (${t(status.substatus)})`
        } else {
            return `${t(status.status)}`
        }
    // we shouldn't go below this
    } else if (status.substatus === 'fully-ready') {
        return t(status.status)
    } else {
        return t(status.substatus)
    }
}

export function isImported(status: IpRightImportStatusInfo) {
    return status.status === 'ready' || status.substatus === 'inactive' || status.substatus === 'has-errors'
}

export function isNotImportable(renewalStatus: IpRightImportStatusInfo) {
    return (renewalStatus.substatus === 'ignored')
}

export function importStatusExtraction(
    {member, ipRight, validationErrors}:
    {member: Member, ipRight?: IpRight, validationErrors?: ValidationMessage[]}
): IpRightImportStatusInfo {
    if (member.countryCode === 'WO') {
        return {status: 'not-handled', substatus: 'ignored', reason: 'pct-not-importable'}
    } else if (ipRight !== undefined) {
        return differentiateImportState({/*member,*/ ipRight, validationErrors})
    } else if (member.familyMemberStatus === 'in-preparation') {
        return {status: 'not-handled', substatus: 'ignored', reason: 'in-preparation'}
    } else if (member.familyMemberStatus === 'stopped') {
        return {status: 'not-handled', substatus: 'stopped'}
    } else if (requiresGrant(member)) {
        return {status: 'not-handled', substatus: 'ignored', reason: 'not-granted'}
    } else if (member.countryCode === 'EP' && member.familyMemberStatus === 'granted' && member.unitaryPatent !== true) {
        return {status: 'not-handled', substatus: 'ignored', reason: 'granted-ep'}
    } else {
        const missing = missingInformation(member)
        // console.log({missing})
        if (missing.length > 0) {
            return { status: 'not-handled', substatus: 'missing-data', messages: missing.filter(m => typeof m === 'string') }
        } else {
            return { status: 'not-handled', substatus: 'can-import' }
        }
    }
}

// function hasRecentChanges(pcIpRight: PcIpRight) {
//     const now = today(getLocalTimeZone())
//     const updated = parseDate(pcIpRight.updated.slice(0, 10))
//     return (now.subtract({ days: 7 }) < updated && !pcIpRight.dirty)
// }

function differentiateImportState({ipRight, validationErrors = []}: {/*member: Member,*/ ipRight?: IpRight, validationErrors?: ValidationMessage[]}): IpRightImportStatusInfo {
    //console.log({ipRight, pcIpRight, validationErrors})
    // Ignore errors if it's actually ready at DM
    /*if (ipRight?.Status?.includes('Granted') && ipRight?.IntegrationStatus === 'Imported') {
        return {status: 'ready', substatus: 'fully-ready'}
    } else*/ if (validationErrors?.length > 0) {
        return {status: 'not-handled', substatus: 'has-errors', messages: validationErrors.map(e => e.message)}
    } else if (ipRight?.status === 'Stopped' || ipRight?.status === 'Expired'){
        // if (pcIpRight.updated !== undefined && (pcIpRight.status === 'Granted' || pcIpRight.status === 'Pending')) {
            // if (hasRecentChanges(pcIpRight)) {
            //     // The IP Right was stopped and now revived again but the back office didn't see it yet
            //     return { status: 'ready', substatus: 'back-office-processing' }
            // }
        // } // default case:
        return { status: 'not-handled', substatus: 'stopped' }
    } else if (ipRight === undefined) {
        return {status: 'ready', substatus: 'back-office-processing'}
    // TODO: look at renewals as well
    // } else if (ipRight?.NextMaintenanceActionDueDate !== undefined && ipRight?.NextMaintenanceActionDueDate === ipRight?.ExpiryDate) {
    //     return {status: 'ready', substatus: 'paid-for-life'}
    } else {
        // if (pcIpRight.status === 'Inactive' && hasRecentChanges(pcIpRight)) {
        //     return { status: 'not-handled', substatus: 'stopped' }
        // }
        // default case
        return {status: 'ready', substatus: 'fully-ready'}
    }
}



// export function isMaintenanceActionPhaseOpen(phase: MaintenanceActionPhase) {
//     return phase === "Auto-Pay in Progress" || phase === "Waiting For Instruction"
// }



export type RenewalNoticeStatus = 
        'not-ready' | 'ready' /* Means no instructions */ | 'instructed' | 'payment-will-be-triggered' | 'payment-has-been-triggered' |
        'fee-will-not-be-paid-unless-instructed' | 'fee-will-not-be-paid' | 'fee-has-not-been-paid'

export type RenewalNoticeStatusInfo = {
    status: RenewalNoticeStatus,
    id?: string,
    instruction?: InstructionType,
    autoPay?: boolean,  
    date?: string,
}

export function renewalNoticeStatusToString(status: RenewalNoticeStatusInfo, t: (key: string, options?: object) => string): string {
    // console.log({status})
    
    const instructed_s = () =>
        (status.instruction === 'Pay')
            ? t('ma.instructed-pay-on', status)
            : (status.instruction === 'Cancel')
            ? t('ma.instructed-cancel-on', status)
            : status.autoPay === true
            ? t('ma.auto-pay', status)
            : status.autoPay === false
            ? t('ma.fee-has-not-been-paid', status)
            : t('ma.unknown-instruction', status)

    switch (status.status) {
        case 'not-ready': return '' // This should never happen...
        case 'instructed': return instructed_s()
        case 'payment-will-be-triggered': return t('ma.payment-will-be-triggered-on', status)
        case 'payment-has-been-triggered': return t('ma.payment-has-been-triggered-on', status)
        case 'fee-will-not-be-paid-unless-instructed': return t('ma.fee-payment-no-instructed-yet')
        case 'fee-will-not-be-paid': return t('ma.fee-will-not-be-paid')
        case 'fee-has-not-been-paid': return t('ma.fee-has-not-been-paid')
        case 'ready': return t('ready')
    }
}


// Make sure to run `eliminateDoubleMaintenanceActions` first on the maintenanceActions before passing them in
export function renewalNoticesStatus({
    renewalStatus, renewals,
}: {
    renewalStatus: IpRightImportStatus, 
    renewals?: Renewal[],
}): RenewalNoticeStatusInfo & {total: number} {
    if ((renewals ?? []).length === 0) {
        const status = renewalStatus === 'ready' ? 'ready' : 'not-ready'
        return {status, total: 0}
    } else {
        const lastRenewal = _.maxBy(renewals, ma => `${ma.instructionDueDate}-${ma.instruction?.created ?? 0}`)
        const status = renewalStatus === 'ready' ? 'ready' : 'not-ready'
        if (!lastRenewal) return {status, total: 0}
        return {
            ...renewalNoticeStatus({ renewal: {...lastRenewal} }), 
            total: renewals?.length ?? 0,
            id: lastRenewal.id,
        }
    }
}

function renewalNoticeStatus(
    { renewal }: 
    { renewal: Renewal & {status?: DueDateStatus} }
): RenewalNoticeStatusInfo {

    // if there is no instruction, let's not consider it as instructed although DM says so
    // const isInstructed = (maintenanceAction.Phase === "Instructed / In Progress" && (instruction !== undefined || maintenanceAction.InstructionInfo !== undefined)) 
    //     || maintenanceAction.Phase === "Closed" || instruction !== undefined
    const isInstructed = renewal.instruction !== undefined || renewal.state === 'Instructed'

    const instruction = renewal.instruction

    if (isInstructed) {
        if (renewal.instruction?.instruction === 'Pay') {
            return {status: 'instructed', instruction: 'Pay', date: instruction?.created?.slice(0, 10)}
        } else if (renewal.instruction?.instruction === 'Cancel') {
            return {status: 'instructed', instruction: 'Cancel', date: instruction?.created?.slice(0, 10)}
        } else {
            return {status: 'instructed', date: instruction?.created?.slice(0, 10), autoPay: renewal.isAutoPay}
        }
    } else {
        if (renewal.isAutoPay) { // auto-pay
            if (renewal.state === 'Cancelled') {
                return { status: 'fee-will-not-be-paid' }
            }
            const today = new Date().toISOString().slice(0, 10)
            const isPast = renewal.instructionDueDate <= today
            return { status: isPast ? 'payment-has-been-triggered' : 'payment-will-be-triggered', date: renewal.instructionDueDate }
        } else {
            if (renewal?.status === 'open' || renewal?.status === 'early' || renewal?.status === 'critical') {
                return { status: 'fee-will-not-be-paid-unless-instructed' }
            } else if (renewal?.status === 'manual-instruction') {
                return { status: 'fee-will-not-be-paid' }
            } else {
                return { status: 'fee-has-not-been-paid' }
            }
        }
    }
}

export const renewalChangeModalStr = 'renewal-change'
export type RenewalChange = 'can-be-imported' | 'has-been-stopped' | undefined

export function canBeImported(
    {oldMember, newMember, ipRight}:
    {oldMember?: Member, newMember: Member, ipRight: IpRight, validationErrors?: ValidationMessage[]}) {
    const newStatus = importStatusExtraction({member: newMember, ipRight})
    //console.log({newStatus})
    if (newStatus.substatus === 'can-import') {
        if(oldMember === undefined) {
            return true
        }
        const oldStatus = importStatusExtraction({member: oldMember, ipRight})
        //console.log({oldStatus})
        //console.log({oldStatus, result: !(oldStatus.status === 'can-import' || isImported(oldStatus.status, ipRight)), imported: isImported(oldStatus.status, ipRight)})
        return !(oldStatus.substatus === 'can-import' || isImported(oldStatus))
    } else {
        return false
    }
}

export function canBeStopped(
    {oldMember, newMember, ipRight, validationErrors}: 
    { oldMember?: Member, newMember: Member, ipRight?: IpRight, validationErrors?: ValidationMessage[] }) {

    if (oldMember?.familyMemberId === undefined) return false

    const oldStatus = importStatusExtraction({ member: oldMember, ipRight, validationErrors })
    return oldStatus.status === 'ready' && newMember.familyMemberStatus === 'stopped' && oldMember.familyMemberStatus !== 'stopped'
}